import type {RefObject} from 'react';
import type {AppDispatch} from '@/store';
import type {BatchMetricsKickoffResponse, MetricsSnapshot} from '@Components/social-media/social-media-metrics-refresh.types';
import type {SocialPost} from '@Components/social-media/post.vo';
import {SocialPostEvent} from '@Components/social-media/social-media.types';
import {asyncnoop} from '@Utils/general.util';
import {mergeRefreshedPosts, unmarkPostsAsRefreshing} from '@Components/social-media/social-media-reducer';
import {updatePostAfterMetricsRefresh} from '@Panels/social-posts-dialog-panel/social-posts-dialog-panel.reducer';

const AUTO_METRICS_POLL_TIMEOUT_MS = 4 * 60 * 1000;

/**
 * Kicks off an on-view auto-metrics refresh for the given posts, then long-polls until each settles.
 * Each poll wave returns the posts settled so far — they are merged, un-skeletoned immediately, and
 * dropped from the pending set — so every row updates the moment its metrics land, over a single
 * connection (a handful of sequential requests, not one per row), until the pending set empties or the
 * timeout is hit.
 */
export const runAutoMetricsRefreshBatch = async (
  postIds: string[],
  eventId: string,
  dispatch: AppDispatch,
  postsRef: RefObject<SocialPost[]>,
  dialogPostIdRef: RefObject<string | null>,
  abortedRef: RefObject<boolean>
): Promise<void> => {
  // postIds were already marked refreshing (skeleton shown) by the hook's layout effect.
  try {
    const kickoff = (await window.PMW.writeLocal('socialmedia/requestAutoMetricsRefresh', {postIds})) as BatchMetricsKickoffResponse;
    const enqueuedPostIds = kickoff?.enqueuedPostIds ?? [];

    // Posts we optimistically skeleton'd but the server didn't enqueue (already
    // fresh / terminal / rate-limited) revert to their numbers right away.
    const notEnqueuedPostIds = postIds.filter((id) => !enqueuedPostIds.includes(id));
    if (notEnqueuedPostIds.length > 0) {
      dispatch(unmarkPostsAsRefreshing(notEnqueuedPostIds));
    }

    let pendingPostIds = enqueuedPostIds;
    const deadlineMs = performance.now() + AUTO_METRICS_POLL_TIMEOUT_MS;
    while (pendingPostIds.length > 0 && !abortedRef.current) {
      const remainingMs = deadlineMs - performance.now();
      if (remainingMs <= 0) {
        break;
      }

      const wavePostIds = pendingPostIds;
      // eslint-disable-next-line no-await-in-loop -- waves are sequential: each poll narrows the pending set
      const wave = (await window.PMW.pollingAjaxCallAsync(
        asyncnoop,
        () => window.PMW.readLocal('socialmedia/pollBatchMetricsStatus', {postIds: wavePostIds, since: kickoff.since}),
        remainingMs,
        eventId
      )) as MetricsSnapshot[] | undefined;

      if (!wave || wave.length === 0) {
        break;
      }

      applyRefreshedSnapshots(wave, dispatch, postsRef.current, dialogPostIdRef.current);
      const settledPostIds = new Set(wave.map((snapshot) => snapshot.postId));
      dispatch(unmarkPostsAsRefreshing(Array.from(settledPostIds)));
      pendingPostIds = pendingPostIds.filter((id) => !settledPostIds.has(id));
    }
  } catch (e) {
    window.PMW.log(`Auto metrics refresh failed: ${String(e)}`);
  } finally {
    // Backstop: clear any skeleton still showing for this batch (timeout / error).
    dispatch(unmarkPostsAsRefreshing(postIds));
  }
};

export const applyRefreshedSnapshots = (snapshots: MetricsSnapshot[], dispatch: AppDispatch, posts: SocialPost[], dialogPostId: string | null): void => {
  const postsById = new Map(posts.map((post) => [post.id, post]));
  const refreshedPosts: SocialPost[] = [];

  snapshots.forEach((snapshot) => {
    const existing = postsById.get(snapshot.postId);
    if (existing) {
      refreshedPosts.push(mergeSnapshotIntoPost(existing, snapshot));
    }
  });

  if (refreshedPosts.length === 0) {
    return;
  }

  dispatch(mergeRefreshedPosts(refreshedPosts));

  refreshedPosts.forEach((post) => {
    window.dispatchEvent(new CustomEvent(SocialPostEvent.UPDATED, {detail: {postId: post.id, post}}));
    if (post.id === dialogPostId) {
      dispatch(updatePostAfterMetricsRefresh(post));
    }
  });
};

/**
 * Replaces a post's numbers only when the snapshot's timestamp advanced past
 * the post's — otherwise just updates job state, so a terminal/legacy snapshot
 * can't wipe good numbers.
 */
export const mergeSnapshotIntoPost = (post: SocialPost, snapshot: MetricsSnapshot): SocialPost => {
  const merged: SocialPost = {
    ...post,
    metricsJobState: snapshot.metricsJobState ?? post.metricsJobState,
  };

  const hasFreshMetrics =
    snapshot.metrics !== null &&
    snapshot.metricsUpdatedOn !== null &&
    (post.metricsUpdatedOn == null || snapshot.metricsUpdatedOn > post.metricsUpdatedOn);

  if (hasFreshMetrics) {
    merged.metrics = snapshot.metrics ?? post.metrics;
    merged.metricsUpdatedOn = snapshot.metricsUpdatedOn;
  }

  return merged;
};
